import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { trpc } from '@/trpc/client';
import { unifiedDiff } from './diff';

export function CategoryEdit(): JSX.Element {
  const params = useParams();
  const id = params.id ?? '';
  const { user } = useAuth();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.prompts.category.get.useQuery({ id });
  const [displayDraft, setDisplayDraft] = useState<string | null>(null);
  const [hintDraft, setHintDraft] = useState<string | null>(null);
  const [bodyDraft, setBodyDraft] = useState<string | null>(null);
  const [kindDraft, setKindDraft] = useState<'normal' | 'optout' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const refresh = async () => {
    await utils.prompts.category.list.invalidate();
    await utils.prompts.category.get.invalidate({ id });
  };

  const update = trpc.prompts.category.update.useMutation({
    onSuccess: async () => {
      await refresh();
      setConfirming(false);
      setDisplayDraft(null);
      setHintDraft(null);
      setBodyDraft(null);
      setKindDraft(null);
      navigate('/prompts');
    },
  });
  const del = trpc.prompts.category.delete.useMutation({
    onSuccess: async () => {
      await refresh();
      setConfirmingDelete(false);
      navigate('/prompts');
    },
  });
  const restore = trpc.prompts.category.restore.useMutation({
    onSuccess: async () => {
      await refresh();
    },
  });

  const canEdit = user?.role === 'editor' || user?.role === 'admin';
  const isDeleted = data ? data.deletedAt !== '_active' : false;
  const editable = canEdit && !isDeleted;

  const displayName = displayDraft ?? data?.displayName ?? '';
  const classifierHint = hintDraft ?? data?.classifierHint ?? '';
  const body = bodyDraft ?? data?.body ?? '';
  const kind: 'normal' | 'optout' = kindDraft ?? data?.kind ?? 'normal';

  const patch: {
    displayName?: string;
    classifierHint?: string;
    body?: string;
    kind?: 'normal' | 'optout';
  } = {};
  if (data && displayName !== data.displayName) patch.displayName = displayName;
  if (data && classifierHint !== data.classifierHint) patch.classifierHint = classifierHint;
  if (data && body !== data.body) patch.body = body;
  if (data && kind !== (data.kind ?? 'normal')) patch.kind = kind;
  const hasChanges = Object.keys(patch).length > 0;
  const saveDisabled =
    !editable ||
    displayName.trim().length === 0 ||
    classifierHint.trim().length === 0 ||
    body.trim().length === 0 ||
    !hasChanges;

  if (isLoading) return <p>読み込み中...</p>;
  if (!data) return <p>カテゴリが見つかりません</p>;

  return (
    <div className="max-w-3xl">
      <header className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold">{data.displayName}</h1>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={() => navigate(`/prompts/categories/${id}/history`)}
          >
            履歴
          </button>
          {canEdit && isDeleted && (
            <button
              type="button"
              className="rounded border px-3 py-1 text-sm"
              disabled={restore.isPending}
              onClick={() => restore.mutate({ id })}
            >
              復元
            </button>
          )}
          {canEdit && !isDeleted && (
            <>
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm text-red-600"
                onClick={() => setConfirmingDelete(true)}
              >
                削除
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:bg-gray-300"
                disabled={saveDisabled}
                onClick={() => setConfirming(true)}
              >
                保存
              </button>
            </>
          )}
        </div>
      </header>
      {isDeleted && (
        <p className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">
          このカテゴリは削除済みです。編集するには復元してください。
        </p>
      )}
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold">表示名</span>
          <input
            type="text"
            className="w-full rounded border p-2 text-sm"
            readOnly={!editable}
            value={displayName}
            onChange={(e) => setDisplayDraft(e.target.value)}
          />
        </label>
        <fieldset className="block">
          <legend className="mb-1 text-sm font-semibold">カテゴリ種別</legend>
          <label className="mr-4 inline-flex items-center gap-1 text-sm">
            <input
              type="radio"
              name="kind"
              value="normal"
              disabled={!editable}
              checked={kind === 'normal'}
              onChange={() => setKindDraft('normal')}
            />
            通常
          </label>
          <label className="inline-flex items-center gap-1 text-sm">
            <input
              type="radio"
              name="kind"
              value="optout"
              disabled={!editable}
              checked={kind === 'optout'}
              onChange={() => setKindDraft('optout')}
            />
            案内停止 (該当時に電話/メールヒアリング → OPT_OUT に遷移)
          </label>
        </fieldset>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold">分類用ヒント</span>
          <span className="mb-1 block text-xs text-gray-500">
            このカテゴリにどんな会話が該当するかを短く説明します。AI
            が会話内容と照らし合わせて該当判定に使います。
          </span>
          <textarea
            className="min-h-[80px] w-full rounded border p-2 text-sm"
            readOnly={!editable}
            value={classifierHint}
            onChange={(e) => setHintDraft(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold">応答生成プロンプト</span>
          <span className="mb-1 block text-xs text-gray-500">
            該当時に AI への指示として追加されます。LINE にそのまま送信される文ではなく、AI
            の応答内容を制御するための指示文を書いてください
            (例:「料金は『担当者から正式に』と添えて具体額を言わない」)。
          </span>
          <textarea
            className="min-h-[200px] w-full rounded border p-2 font-mono text-sm"
            readOnly={!editable}
            value={body}
            onChange={(e) => setBodyDraft(e.target.value)}
          />
        </label>
      </div>

      {confirming && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30">
          <div className="w-[600px] rounded bg-white p-4 shadow">
            <h2 className="mb-2 text-base font-semibold">変更を確認して保存</h2>
            {patch.displayName !== undefined && (
              <p className="mb-1 text-sm">
                表示名: <span className="line-through text-red-600">{data.displayName}</span> →{' '}
                <span className="text-green-700">{patch.displayName}</span>
              </p>
            )}
            {patch.classifierHint !== undefined && (
              <div className="mb-2">
                <div className="mb-1 text-xs font-semibold text-gray-600">分類用ヒント</div>
                <div className="max-h-32 overflow-auto rounded border bg-gray-50 p-2 font-mono text-xs">
                  {unifiedDiff(data.classifierHint, patch.classifierHint).map((line, idx) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: diff lines have no stable id
                      key={idx}
                      className={
                        line.kind === 'add'
                          ? 'bg-green-100'
                          : line.kind === 'remove'
                            ? 'bg-red-100'
                            : ''
                      }
                    >
                      {line.kind === 'add' ? '+ ' : line.kind === 'remove' ? '- ' : '  '}
                      {line.text}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {patch.kind !== undefined && (
              <p className="mb-1 text-sm">
                種別: <span className="line-through text-red-600">{data.kind ?? 'normal'}</span> →{' '}
                <span className="text-green-700">{patch.kind}</span>
              </p>
            )}
            {patch.body !== undefined && (
              <div className="mb-2">
                <div className="mb-1 text-xs font-semibold text-gray-600">応答生成プロンプト</div>
                <div className="max-h-64 overflow-auto rounded border bg-gray-50 p-2 font-mono text-xs">
                  {unifiedDiff(data.body, patch.body).map((line, idx) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: diff lines have no stable id
                      key={idx}
                      className={
                        line.kind === 'add'
                          ? 'bg-green-100'
                          : line.kind === 'remove'
                            ? 'bg-red-100'
                            : ''
                      }
                    >
                      {line.kind === 'add' ? '+ ' : line.kind === 'remove' ? '- ' : '  '}
                      {line.text}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm"
                onClick={() => setConfirming(false)}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white"
                onClick={() => update.mutate({ id, ...patch })}
                disabled={update.isPending}
              >
                保存して反映
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmingDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30">
          <div className="w-[480px] rounded bg-white p-4 shadow">
            <h2 className="mb-2 text-base font-semibold">カテゴリを削除しますか?</h2>
            <p className="mb-3 text-sm text-gray-600">
              「{data.displayName}」を削除します。後から復元できます。
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm"
                onClick={() => setConfirmingDelete(false)}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="rounded bg-red-600 px-3 py-1 text-sm text-white"
                disabled={del.isPending}
                onClick={() => del.mutate({ id })}
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
